import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Drawer from "@material-ui/core/Drawer";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";

import EventBusyIcon from "@material-ui/icons/EventBusy";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import AddIcon from "@material-ui/icons/Add";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import BlockIcon from "@material-ui/icons/Block";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ViewListIcon from "@material-ui/icons/ViewList";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightSmallIcon from "@material-ui/icons/KeyboardArrowRight";

import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import ptBR from "date-fns/locale/pt-BR";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import listPageStyles from "../../styles/listPageStyles";
import sidebarScrollbar from "../../helpers/sidebarScrollbar";

import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";
import formatPhone from "../../helpers/formatPhone";
import NewAppointmentModal from "../../components/AppointmentModal";
import RescheduleDialog from "../../components/AppointmentModal/RescheduleDialog";
import PaymentDialog from "../../components/AppointmentModal/PaymentDialog";

const locales = { "pt-BR": ptBR };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const DnDCalendar = withDragAndDrop(Calendar);

const calendarMessages = {
  next: "Próximo",
  previous: "Anterior",
  today: "Hoje",
  month: "Mês",
  week: "Semana",
  day: "Dia",
  agenda: "Agenda",
  date: "Data",
  time: "Hora",
  event: "Agendamento",
  noEventsInRange: "Nenhum agendamento neste período.",
  showMore: (total) => `+${total} mais`,
  allDay: "Dia todo",
};

const WEEKDAYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

const dayLabel = (dateStr) => {
  const m = moment(dateStr, "YYYY-MM-DD");
  const today = moment().startOf("day");
  const diff = m.diff(today, "days");
  const base = `${WEEKDAYS[m.day()]}, ${m.date()} ${MONTHS[m.month()]}`;
  if (diff === 0) return `Hoje · ${base}`;
  if (diff === 1) return `Amanhã · ${base}`;
  if (diff === -1) return `Ontem · ${base}`;
  return base;
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const reducer = (state, action) => {
  if (action.type === "LOAD_APPOINTMENTS") {
    const appointments = action.payload;
    const newItems = [];
    appointments.forEach((appt) => {
      const idx = state.findIndex((a) => a.id === appt.id);
      if (idx !== -1) {
        state[idx] = appt;
      } else {
        newItems.push(appt);
      }
    });
    return [...state, ...newItems];
  }

  if (action.type === "UPDATE_APPOINTMENT") {
    const appt = action.payload;
    const idx = state.findIndex((a) => a.id === appt.id);
    if (idx !== -1) {
      state[idx] = { ...state[idx], ...appt };
      return [...state];
    }
    return [appt, ...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

// "pendente" continua sendo o valor salvo no banco; só o rótulo mudou.
const STATUS_META = {
  pendente:   { label: "A confirmar", color: "#E07A00", text: "#9A4A0B", bg: "#FFF4E5", darkText: "#FFB74D" },
  confirmado: { label: "Confirmado",  color: "#2D7BE8", text: "#1F5FB8", bg: "#EAF2FD", darkText: "#8DB5EE" },
  concluido:  { label: "Concluído",   color: "#2E8B47", text: "#1E6B33", bg: "#E8F5EC", darkText: "#81C995" },
  faltou:     { label: "Faltou",      color: "#D93025", text: "#B3261E", bg: "#FDECEC", darkText: "#FF8A80" },
  cancelado:  { label: "Cancelado",   color: "#8A9099", text: "#5F6670", bg: "#F1F3F5", darkText: "#B0B6BD" },
};

// Espelha a mesma matriz do backend (UpdateAppointmentService).
// "concluido", "faltou" e "cancelado" são terminais — sem transições.
const STATUS_TRANSITIONS = {
  pendente:   ["confirmado", "concluido", "faltou", "cancelado"],
  confirmado: ["pendente",   "concluido", "faltou", "cancelado"],
  concluido:  [],
  faltou:     [],
  cancelado:  [],
};

const canTransitionTo = (currentStatus, targetStatus) =>
  (STATUS_TRANSITIONS[currentStatus] || []).includes(targetStatus);

const TERMINAL_STATUSES = ["concluido", "faltou", "cancelado"];

const STATUS_TABS = [
  { key: "", label: "Todos" },
  { key: "pendente", label: "A confirmar" },
  { key: "confirmado", label: "Confirmados" },
  { key: "concluido", label: "Concluídos" },
  { key: "faltou", label: "Faltas" },
  { key: "cancelado", label: "Cancelados" },
];

const PERIODS = [
  { key: "today", label: "Hoje" },
  { key: "week", label: "Esta semana" },
  { key: "next7", label: "Próximos 7 dias" },
  { key: "month", label: "Este mês" },
];

const periodRange = (key) => {
  const now = moment();
  const f = "YYYY-MM-DD";
  if (key === "today") return [now.format(f), now.format(f)];
  if (key === "next7") return [now.format(f), now.clone().add(6, "days").format(f)];
  if (key === "month") return [now.clone().startOf("month").format(f), now.clone().endOf("month").format(f)];
  return [now.clone().startOf("week").format(f), now.clone().endOf("week").format(f)];
};

// Intervalo de datas que o calendário mostra para (visão, data).
const calendarRange = (view, date) => {
  const f = "YYYY-MM-DD";
  const m = moment(date);
  if (view === "month") {
    return [m.clone().startOf("month").startOf("week").format(f), m.clone().endOf("month").endOf("week").format(f)];
  }
  if (view === "week") return [m.clone().startOf("week").format(f), m.clone().endOf("week").format(f)];
  if (view === "agenda") return [m.format(f), m.clone().add(29, "days").format(f)];
  return [m.format(f), m.format(f)];
};

const calendarLabel = (view, date) => {
  const m = moment(date);
  const monthName = (x) => MONTHS[x.month()];
  const fullMonth = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
  ];
  if (view === "month") return `${fullMonth[m.month()]} ${m.year()}`;
  if (view === "week") {
    const s = m.clone().startOf("week");
    const e = m.clone().endOf("week");
    return `${s.date()} ${monthName(s)} – ${e.date()} ${monthName(e)} ${e.year()}`;
  }
  if (view === "agenda") {
    const e = m.clone().add(29, "days");
    return `${m.date()} ${monthName(m)} – ${e.date()} ${monthName(e)} ${e.year()}`;
  }
  return `${WEEKDAYS[m.day()]}, ${m.date()} de ${fullMonth[m.month()].toLowerCase()}`;
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    "@global": {
      ".rbc-overlay": {
        zIndex: "1400 !important",
        backgroundColor: `${theme.palette.background.paper} !important`,
        color: `${theme.palette.text.primary} !important`,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "10px !important",
        boxShadow: "0 8px 32px rgba(0,0,0,0.24) !important",
        ...sidebarScrollbar(theme),
      },
      ".rbc-overlay-header": {
        fontWeight: "600 !important",
        color: `${theme.palette.text.primary} !important`,
        borderBottomColor: `${theme.palette.divider} !important`,
      },
    },
    ...listPageStyles(theme),
    root: { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 },
    viewToggle: {
      display: "flex",
      borderRadius: 10,
      overflow: "hidden",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
    },
    viewButton: {
      height: 38,
      padding: theme.spacing(0, 1.75),
      border: 0,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 6,
      background: "transparent",
      color: theme.palette.text.secondary,
      font: "600 13px Inter, Roboto, sans-serif",
      "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
    },
    viewButtonActive: {
      backgroundColor: theme.palette.text.primary,
      color: theme.palette.background.paper,
    },
    warn: { color: dark ? "#FFB74D" : "#9A4A0B", fontWeight: 600 },
    body: { display: "flex", gap: theme.spacing(2), flex: 1, minHeight: 0 },
    mainPaper: {
      ...listPageStyles(theme).mainPaper,
      minWidth: 0,
    },
    // ----- filtros -----
    filterBar: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    filterLabel: {
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      marginRight: 2,
    },
    pill: {
      height: 32,
      padding: theme.spacing(0, 1.75),
      borderRadius: 16,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
      font: "500 13px Inter, Roboto, sans-serif",
      "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
    },
    pillActive: {
      backgroundColor: theme.palette.text.primary,
      borderColor: theme.palette.text.primary,
      color: theme.palette.background.paper,
      fontWeight: 600,
    },
    pillCount: { opacity: 0.75 },
    dot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
    avatar: {
      width: 24,
      height: 24,
      borderRadius: "50%",
      fontSize: 10,
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
      color: dark ? "#CFE0FA" : theme.palette.primary.dark,
    },
    avatarLg: { width: 48, height: 48, fontSize: 17 },
    // ----- calendário -----
    calToolbar: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    navButton: {
      width: 32,
      height: 32,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      padding: 0,
    },
    todayButton: {
      textTransform: "none",
      fontWeight: 600,
      borderRadius: 8,
      height: 32,
      borderColor: theme.palette.divider,
      color: theme.palette.text.primary,
    },
    calLabel: { fontSize: 17, fontWeight: 700, margin: theme.spacing(0, 0.5) },
    seg: {
      display: "flex",
      borderRadius: 8,
      overflow: "hidden",
      border: `1px solid ${theme.palette.divider}`,
    },
    segButton: {
      height: 30,
      padding: theme.spacing(0, 1.5),
      border: 0,
      borderLeft: `1px solid ${theme.palette.divider}`,
      cursor: "pointer",
      background: "transparent",
      color: theme.palette.text.primary,
      font: "500 13px Inter, Roboto, sans-serif",
      "&:first-child": { borderLeft: 0 },
    },
    segButtonActive: {
      backgroundColor: alpha(theme.palette.primary.main, dark ? 0.28 : 0.12),
      color: dark ? "#CFE0FA" : theme.palette.primary.dark,
      fontWeight: 600,
    },
    calendarWrapper: {
      flex: 1,
      minHeight: 420,
      padding: theme.spacing(0, 1.5),
      overflow: "hidden",
      "& .rbc-calendar": { fontFamily: "inherit" },
      "& .rbc-event": { borderRadius: 7, padding: "2px 6px", outline: "none" },
      "& .rbc-event.rbc-selected": { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` },
      "& .rbc-month-view .rbc-event": {
        height: "auto",
        padding: "2px 6px",
        whiteSpace: "normal",
      },
      "& .rbc-month-view .rbc-event-content": { whiteSpace: "normal" },
      "& .rbc-month-view .rbc-row-segment": { padding: "0 2px 2px" },
      "& .rbc-show-more": {
        color: dark ? "#8DB5EE" : theme.palette.primary.main,
        fontWeight: 700,
        fontSize: "0.75rem",
      },
      "& .rbc-off-range-bg": { backgroundColor: dark ? "#1a1a1a" : "#F7F8FA" },
      "& .rbc-off-range": { color: theme.palette.text.disabled },
      "& .rbc-today": { backgroundColor: dark ? "#1a2744" : "#F5F9FE" },
      "& .rbc-header": {
        borderBottomColor: theme.palette.divider,
        color: theme.palette.text.primary,
        padding: "8px 4px",
        fontWeight: 600,
        fontSize: 12,
      },
      "& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view": {
        borderColor: theme.palette.divider,
        borderRadius: 10,
        overflow: "hidden",
      },
      "& .rbc-day-bg + .rbc-day-bg": { borderColor: theme.palette.divider },
      "& .rbc-month-row + .rbc-month-row": { borderColor: theme.palette.divider },
      "& .rbc-header + .rbc-header": { borderColor: theme.palette.divider },
      "& .rbc-time-content, & .rbc-time-header-content, & .rbc-time-content > * + * > *": {
        borderColor: theme.palette.divider,
      },
      "& .rbc-timeslot-group": { borderColor: theme.palette.divider },
      "& .rbc-day-slot .rbc-time-slot": { borderColor: dark ? "#2a2a2a" : "#EDEFF2" },
      "& .rbc-time-header-content": { borderLeftColor: theme.palette.divider },
      "& .rbc-label, & .rbc-time-slot": { color: theme.palette.text.secondary, fontSize: "0.72rem" },
      "& .rbc-date-cell": { color: theme.palette.text.primary, padding: "4px 6px", fontWeight: 600 },
      "& .rbc-date-cell.rbc-now": { color: theme.palette.primary.main },
      "& .rbc-current-time-indicator": { backgroundColor: "#D93025", height: 2 },
      // Barra de rolagem igual à do menu lateral (fina, aparece ao passar o mouse).
      // O cabeçalho reserva o mesmo espaço (4px) para as colunas alinharem.
      "& .rbc-time-content": { ...sidebarScrollbar(theme) },
      "& .rbc-agenda-content": { ...sidebarScrollbar(theme) },
      "& .rbc-time-header": { marginRight: "4px !important", overflowY: "hidden" },
      "& .rbc-time-header.rbc-overflowing": { borderRightColor: "transparent" },
      "& .rbc-addons-dnd-drag-preview": { opacity: 0.8 },
      "& .rbc-agenda-date-cell, & .rbc-agenda-time-cell, & .rbc-agenda-event-cell": {
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
      },
      "& .rbc-agenda-table": { borderColor: theme.palette.divider },
      "& .rbc-agenda-table thead tr th": {
        borderBottomColor: theme.palette.divider,
        color: theme.palette.text.secondary,
      },
    },
    legend: {
      display: "flex",
      gap: theme.spacing(2),
      flexWrap: "wrap",
      alignItems: "center",
      padding: theme.spacing(1.25, 2),
      borderTop: `1px solid ${theme.palette.divider}`,
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    legendItem: { display: "flex", alignItems: "center", gap: 6 },
    eventCard: { display: "flex", flexDirection: "column", lineHeight: 1.2, overflow: "hidden" },
    eventTopRow: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      overflow: "hidden",
      whiteSpace: "nowrap",
      fontSize: "0.7rem",
      fontWeight: 700,
    },
    eventPro: { marginLeft: "auto", fontSize: "0.62rem", opacity: 0.8, color: theme.palette.text.primary },
    eventName: {
      fontWeight: 600,
      fontSize: "0.75rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      color: theme.palette.text.primary,
    },
    eventService: {
      fontSize: "0.68rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      color: theme.palette.text.secondary,
    },
    // ----- mobile -----
    weekStrip: { display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 4 },
    stripDay: {
      height: 58,
      border: 0,
      borderRadius: 12,
      cursor: "pointer",
      padding: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      background: "transparent",
      color: theme.palette.text.primary,
      font: "inherit",
    },
    stripDayActive: { backgroundColor: theme.palette.primary.main, color: "#fff" },
    mobileCard: {
      display: "flex",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      borderRadius: 12,
      cursor: "pointer",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
    },
    historyTiles: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: theme.spacing(1) },
    historyTile: {
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 10,
      backgroundColor: theme.palette.action.hover,
    },
    // ----- painel "A confirmar" -----
    side: {
      width: 310,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      minHeight: 0,
      [theme.breakpoints.down("md")]: { display: "none" },
    },
    sideCard: {
      borderRadius: 14,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
    },
    stats: {
      padding: theme.spacing(2),
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: theme.spacing(1),
    },
    statsTitle: {
      gridColumn: "1 / -1",
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    statValue: { fontSize: 20, fontWeight: 700, lineHeight: 1.2 },
    statLabel: { fontSize: 12, color: theme.palette.text.secondary },
    pendingCard: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" },
    pendingHead: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1.75, 2, 1.25),
      borderBottom: `1px solid ${theme.palette.divider}`,
      "& h2": { margin: 0, fontSize: 15, fontWeight: 700, flex: 1 },
    },
    countBadge: {
      minWidth: 22,
      height: 22,
      padding: "0 7px",
      boxSizing: "border-box",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.text.primary,
    },
    pendingList: {
      flex: 1,
      overflowY: "auto",
      padding: theme.spacing(1),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.75),
      ...sidebarScrollbar(theme),
    },
    pendingItem: {
      padding: theme.spacing(1.25),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 10,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
      cursor: "pointer",
      "&:hover": { borderColor: theme.palette.text.disabled },
    },
    confirmButton: {
      textTransform: "none",
      fontWeight: 600,
      borderRadius: 8,
      height: 30,
      backgroundColor: alpha(theme.palette.primary.main, dark ? 0.25 : 0.12),
      color: dark ? "#CFE0FA" : theme.palette.primary.dark,
      "&:hover": { backgroundColor: alpha(theme.palette.primary.main, dark ? 0.35 : 0.2) },
    },
    // ----- lista -----
    listHeaderRow: {
      display: "grid",
      gridTemplateColumns: "96px minmax(0,1.5fr) minmax(0,1.1fr) 150px 100px 124px 190px",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.25, 2.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    listRow: {
      display: "grid",
      gridTemplateColumns: "96px minmax(0,1.5fr) minmax(0,1.1fr) 150px 100px 124px 190px",
      gap: theme.spacing(1.5),
      alignItems: "center",
      padding: theme.spacing(1.25, 2.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      cursor: "pointer",
      transition: "background-color .12s",
      "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "72px minmax(0,1fr) auto",
        gridTemplateAreas: '"time who status" "time svc status" "time pro actions"',
        rowGap: 2,
        "& > [data-col=price]": { display: "none" },
      },
    },
    groupHeader: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      width: "100%",
      border: 0,
      cursor: "pointer",
      background: "transparent",
      padding: theme.spacing(1.75, 2.5, 1),
      color: theme.palette.text.secondary,
      font: "600 12px Inter, Roboto, sans-serif",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      textAlign: "left",
      "&::after": { content: '""', flex: 1, height: 1, backgroundColor: theme.palette.divider },
    },
    groupBadge: {
      minWidth: 20,
      height: 20,
      padding: "0 6px",
      boxSizing: "border-box",
      borderRadius: 999,
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.text.primary,
      fontSize: 11,
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      letterSpacing: 0,
    },
    statusChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      height: 24,
      padding: theme.spacing(0, 1.25),
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    rowActions: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 },
    rowAction: {
      textTransform: "none",
      fontWeight: 600,
      borderRadius: 8,
      height: 32,
      whiteSpace: "nowrap",
      borderColor: theme.palette.divider,
      color: theme.palette.text.primary,
    },
    nameCell: { fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    subCell: { fontSize: 12, color: theme.palette.text.secondary },
    // ----- detalhe -----
    drawerPaper: {
      width: 460,
      maxWidth: "100vw",
      color: theme.palette.text.primary,
      display: "flex",
      flexDirection: "column",
    },
    drawerHead: {
      padding: theme.spacing(2.5, 3, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.75),
    },
    drawerBody: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      padding: theme.spacing(2.5, 3),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2.5),
      ...sidebarScrollbar(theme),
    },
    drawerFoot: {
      padding: theme.spacing(2, 3),
      borderTop: `1px solid ${theme.palette.divider}`,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.25),
    },
    sectionTitle: {
      margin: 0,
      marginBottom: theme.spacing(1),
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    infoTable: { border: `1px solid ${theme.palette.divider}`, borderRadius: 12, overflow: "hidden" },
    infoRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.25, 1.75),
      borderBottom: `1px solid ${theme.palette.divider}`,
      "&:last-child": { borderBottom: 0 },
    },
    infoKey: { width: 110, flexShrink: 0, fontSize: 13, color: theme.palette.text.secondary },
    infoVal: { flex: 1, fontSize: 14, fontWeight: 600 },
    infoSub: { fontSize: 12.5, color: theme.palette.text.secondary },
    primaryAction: {
      textTransform: "none",
      fontWeight: 600,
      borderRadius: 10,
      height: 42,
    },
    footRow: { display: "flex", gap: theme.spacing(1) },
    footButton: {
      flex: 1,
      textTransform: "none",
      fontWeight: 600,
      borderRadius: 10,
      height: 38,
      borderColor: theme.palette.divider,
    },
    emptyState: { ...listPageStyles(theme).emptyState },
    emptyIcon: { ...listPageStyles(theme).emptyIcon },
  };
});

const AppointmentsPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const dark = theme.palette.type === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { user } = useContext(AuthContext);
  const isAdmin = user.profile === "admin";

  const [loading, setLoading] = useState(false);
  const [appointments, dispatch] = useReducer(reducer, []);
  const [viewMode, setViewMode] = useState("calendar");
  const [statusFilter, setStatusFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState(isAdmin ? "" : String(user.id));
  const [providers, setProviders] = useState([]);
  const [showCancelled, setShowCancelled] = useState(false);
  const [search, setSearch] = useState("");

  // Lista
  const [period, setPeriod] = useState("week");
  const [collapsed, setCollapsed] = useState({});

  // Calendário (controlado: a gente calcula o intervalo a buscar)
  const [calView, setCalView] = useState("week");
  const [teamMode, setTeamMode] = useState(false); // dia com uma coluna por profissional
  const [clientHistory, setClientHistory] = useState(null);
  const [calDate, setCalDate] = useState(new Date());

  const [confirmModal, setConfirmModal] = useState({ open: false, appt: null, status: null });
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [menu, setMenu] = useState({ anchor: null, appt: null });
  const [newModal, setNewModal] = useState({ open: false, date: null });
  const [rescheduling, setRescheduling] = useState(null);
  const [paying, setPaying] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const fetchRef = useRef(null);

  const [range, setRange] = useState(() => periodRange("week"));

  useEffect(() => {
    api
      .get("/users/list")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data?.users || [];
        setProviders(list.filter((u) => u.isServiceProvider));
      })
      .catch(() => {});
  }, []);

  // Intervalo a buscar conforme a visão ativa.
  useEffect(() => {
    setRange(viewMode === "calendar" ? calendarRange(calView, calDate) : periodRange(period));
  }, [viewMode, calView, calDate, period]);

  const fetchAppointments = useCallback(async () => {
    try {
      const [from, to] = range;
      let page = 1;
      let all = [];
      let more = true;
      while (more && page <= 40) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get("/appointments", {
          params: {
            dateFrom: from,
            dateTo: to,
            userId: providerFilter || undefined,
            pageNumber: page,
          },
        });
        all = all.concat(data.appointments);
        more = data.hasMore;
        page += 1;
      }

      dispatch({ type: "RESET" });
      dispatch({ type: "LOAD_APPOINTMENTS", payload: all });
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  }, [range, providerFilter]);

  fetchRef.current = fetchAppointments;

  // Bloqueios de horário (almoço, folga…) para desenhar no calendário.
  useEffect(() => {
    if (viewMode !== "calendar" || isMobile) {
      setBlocks([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get("/schedule-blocks", {
        params: { dateFrom: range[0], dateTo: range[1], userId: providerFilter || undefined },
      })
      .then(({ data }) => !cancelled && setBlocks(data.blocks || []))
      .catch(() => !cancelled && setBlocks([]));
    return () => {
      cancelled = true;
    };
  }, [viewMode, isMobile, range, providerFilter]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAppointments();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchAppointments]);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });

    socket.on("appointment", (data) => {
      if (data.action === "create") {
        // O evento de criação vem sem os dados do contato: recarrega o período.
        fetchRef.current && fetchRef.current();
      } else if (data.action === "update") {
        dispatch({ type: "UPDATE_APPOINTMENT", payload: data.appointment });
        setDetail((d) => (d && d.id === data.appointment.id ? { ...d, ...data.appointment } : d));
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [user]);

  useEffect(() => {
    setNotes(detail?.notes || "");
    // só ao abrir outro agendamento
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id]);

  // Histórico do cliente (todos os agendamentos dele) para o painel de detalhe.
  useEffect(() => {
    const contactId = detail?.contact?.id;
    setClientHistory(null);
    if (!contactId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        let page = 1;
        let all = [];
        let more = true;
        while (more && page <= 10) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await api.get("/appointments", { params: { contactId, pageNumber: page } });
          all = all.concat(data.appointments);
          more = data.hasMore;
          page += 1;
        }
        if (cancelled) return;
        const done = all.filter((a) => a.status === "concluido");
        setClientHistory({
          done: done.length,
          missed: all.filter((a) => a.status === "faltou").length,
          last: done.length ? done[done.length - 1].startAt : null,
        });
      } catch (err) {
        // histórico é complementar: falha silenciosa
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detail?.contact?.id]);

  const updateStatus = async (appt, status) => {
    try {
      await api.put(`/appointments/${appt.id}`, { status });
      toast.success("Agendamento atualizado.");
      const next = { ...appt, status };
      dispatch({ type: "UPDATE_APPOINTMENT", payload: next });
      setDetail((d) => (d && d.id === appt.id ? next : d));
      // Ao concluir, o backend gera a receita: recarrega para mostrar o pagamento.
      if (status === "concluido") fetchRef.current && fetchRef.current();
    } catch (err) {
      toastError(err);
    }
    setConfirmModal({ open: false, appt: null, status: null });
  };

  // Confirmar presença não precisa de modal de confirmação — é a ação de rotina.
  const confirmNow = (appt) => updateStatus(appt, "confirmado");

  const askConfirm = (appt, status) => setConfirmModal({ open: true, appt, status });

  const saveNotes = async () => {
    if (!detail) return;
    setSavingNotes(true);
    try {
      await api.put(`/appointments/${detail.id}`, { notes });
      const next = { ...detail, notes };
      dispatch({ type: "UPDATE_APPOINTMENT", payload: next });
      setDetail(next);
      toast.success("Observações salvas.");
    } catch (err) {
      toastError(err);
    }
    setSavingNotes(false);
  };

  const metaOf = (status) => STATUS_META[status] || STATUS_META.cancelado;
  const textOf = (status) => (dark ? metaOf(status).darkText : metaOf(status).text);
  const chipBg = (status) => (dark ? alpha(metaOf(status).color, 0.2) : metaOf(status).bg);

  const providerInitials = (appt) => initials(appt.user?.name);
  const priceOf = (appt) => appt.price ?? appt.service?.price ?? 0;

  // Busca + cancelados (calendário) + status (lista)
  const searched = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return appointments;
    return appointments.filter(
      (a) =>
        a.contact?.name?.toLowerCase().includes(term) ||
        a.contact?.number?.includes(term.replace(/\D/g, "") || "§") ||
        a.service?.name?.toLowerCase().includes(term)
    );
  }, [appointments, search]);

  const counts = useMemo(() => {
    const c = { "": searched.length };
    searched.forEach((a) => {
      c[a.status] = (c[a.status] || 0) + 1;
    });
    return c;
  }, [searched]);

  const calendarAppointments = useMemo(
    () => appointments.filter((a) => showCancelled || a.status !== "cancelado"),
    [appointments, showCancelled]
  );

  const listAppointments = useMemo(
    () => searched.filter((a) => !statusFilter || a.status === statusFilter),
    [searched, statusFilter]
  );

  const groups = useMemo(() => {
    const map = new Map();
    listAppointments.forEach((a) => {
      const key = moment(a.startAt).format("YYYY-MM-DD");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([key, rows]) => ({
        key,
        label: dayLabel(key),
        rows: rows.sort((x, y) => new Date(x.startAt) - new Date(y.startAt)),
      }));
  }, [listAppointments]);

  const todayKey = moment().format("YYYY-MM-DD");
  const todayAppts = useMemo(
    () => appointments.filter((a) => moment(a.startAt).format("YYYY-MM-DD") === todayKey && a.status !== "cancelado"),
    [appointments, todayKey]
  );
  const todayPending = todayAppts.filter((a) => a.status === "pendente").length;
  const todayDone = todayAppts.filter((a) => a.status === "concluido").length;
  const todayValue = todayAppts.reduce((sum, a) => sum + Number(priceOf(a)), 0);

  const pendingList = useMemo(
    () =>
      appointments
        .filter((a) => a.status === "pendente")
        .sort((x, y) => new Date(x.startAt) - new Date(y.startAt)),
    [appointments]
  );

  const listTotals = useMemo(() => {
    const done = listAppointments.filter((a) => a.status === "concluido");
    return { done: done.reduce((s, a) => s + Number(priceOf(a)), 0) };
  }, [listAppointments]);

  const calendarEvents = useMemo(
    () =>
      calendarAppointments.map((appt) => ({
        id: appt.id,
        title: `${appt.contact ? appt.contact.name : "—"}${appt.service ? ` · ${appt.service.name}` : ""}`,
        start: new Date(appt.startAt),
        end: new Date(appt.endAt),
        resourceId: appt.user?.id ?? appt.userId,
        resource: appt,
      })),
    [calendarAppointments]
  );

  // Bloqueios viram faixas hachuradas atrás dos cards. Em Semana/Dia só aparecem
  // com um profissional escolhido (senão pintariam a coluna de todos); na
  // visão Equipe cada bloqueio vai para a coluna do seu profissional.
  const backgroundEvents = useMemo(() => {
    const timeView = calView === "week" || calView === "day";
    if (!timeView || (!providerFilter && !(teamMode && calView === "day"))) return [];
    return blocks.map((b) => {
      const day = moment(b.date, "YYYY-MM-DD");
      const start = b.isFullDay || !b.startTime ? day.clone().startOf("day") : moment(`${b.date} ${b.startTime}`, "YYYY-MM-DD HH:mm");
      const end = b.isFullDay || !b.endTime ? day.clone().endOf("day") : moment(`${b.date} ${b.endTime}`, "YYYY-MM-DD HH:mm");
      return {
        id: `block-${b.id}`,
        title: b.reason || "Bloqueado",
        start: start.toDate(),
        end: end.toDate(),
        resourceId: b.userId,
        resource: { isBlock: true, fullDay: !!b.isFullDay, reason: b.reason },
      };
    });
  }, [blocks, calView, teamMode, providerFilter]);

  const eventStyleGetter = useCallback(
    (event) => {
      if (event.resource?.isBlock) {
        return {
          style: {
            background: `repeating-linear-gradient(135deg, ${dark ? "#2a2d33" : "#F4F5F7"} 0, ${dark ? "#2a2d33" : "#F4F5F7"} 6px, ${dark ? "#33363d" : "#E9EBEE"} 6px, ${dark ? "#33363d" : "#E9EBEE"} 7px)`,
            border: 0,
            borderRadius: 0,
            color: theme.palette.text.secondary,
            opacity: 1,
            cursor: "default",
          },
        };
      }
      const status = event.resource?.status;
      const meta = metaOf(status);
      return {
        style: {
          backgroundColor: dark ? alpha(meta.color, 0.22) : meta.bg,
          border: `1px solid ${alpha(meta.color, dark ? 0.5 : 0.35)}`,
          borderRadius: 7,
          color: theme.palette.text.primary,
          opacity: status === "cancelado" ? 0.7 : 1,
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dark, theme]
  );

  const AppointmentEvent = useCallback(
    ({ event }) => {
      const appt = event.resource || {};
      if (appt.isBlock) {
        return (
          <span className={classes.eventTopRow} style={{ fontWeight: 600, color: theme.palette.text.secondary }}>
            <BlockIcon style={{ fontSize: 13 }} />
            {appt.reason || "Bloqueado"}
          </span>
        );
      }
      const color = textOf(appt.status);
      return (
        <div className={classes.eventCard}>
          <span className={classes.eventTopRow} style={{ color }}>
            <span className={classes.dot} style={{ backgroundColor: metaOf(appt.status).color, width: 6, height: 6 }} />
            {moment(appt.startAt).format("HH:mm")}
            <span className={classes.eventPro}>{providerInitials(appt)}</span>
          </span>
          <span
            className={classes.eventName}
            style={{ textDecoration: appt.status === "cancelado" ? "line-through" : "none" }}
          >
            {appt.contact ? appt.contact.name : "—"}
          </span>
          {appt.service && <span className={classes.eventService}>{appt.service.name}</span>}
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classes, dark, theme]
  );

  const CalendarToolbar = useCallback(
    ({ onNavigate, onView, view, date }) => (
      <div className={classes.calToolbar}>
        <IconButton className={classes.navButton} aria-label="Anterior" onClick={() => onNavigate("PREV")}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <IconButton className={classes.navButton} aria-label="Próximo" onClick={() => onNavigate("NEXT")}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        <span className={classes.calLabel}>{calendarLabel(view, date)}</span>
        <Button variant="outlined" size="small" className={classes.todayButton} onClick={() => onNavigate("TODAY")}>
          Hoje
        </Button>
        <span style={{ flex: 1 }} />
        <div className={classes.seg}>
          {[
            ["day", "Dia"],
            ["week", "Semana"],
            ["month", "Mês"],
            ["agenda", "Agenda"],
            ...(isAdmin && providers.length > 1 ? [["team", "Equipe"]] : []),
          ].map(([key, label]) => {
            const active = key === "team" ? view === "day" && teamMode : key === "day" ? view === "day" && !teamMode : view === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                className={`${classes.segButton} ${active ? classes.segButtonActive : ""}`}
                onClick={() => {
                  setTeamMode(key === "team");
                  onView(key === "team" ? "day" : key);
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    ),
    [classes, teamMode, isAdmin, providers.length]
  );

  const StatusChip = ({ status }) => (
    <span className={classes.statusChip} style={{ backgroundColor: chipBg(status), color: textOf(status) }}>
      <span className={classes.dot} style={{ backgroundColor: metaOf(status).color }} />
      {metaOf(status).label}
    </span>
  );

  const ProviderPills = () =>
    isAdmin && providers.length > 0 ? (
      <>
        <span className={classes.filterLabel}>Profissional</span>
        <button
          type="button"
          aria-pressed={providerFilter === ""}
          className={`${classes.pill} ${providerFilter === "" ? classes.pillActive : ""}`}
          onClick={() => setProviderFilter("")}
        >
          Todos
        </button>
        {providers.map((p) => (
          <button
            key={p.id}
            type="button"
            aria-pressed={String(providerFilter) === String(p.id)}
            className={`${classes.pill} ${String(providerFilter) === String(p.id) ? classes.pillActive : ""}`}
            onClick={() => setProviderFilter(String(p.id))}
          >
            {p.name}
          </button>
        ))}
      </>
    ) : null;

  const rowAction = (appt) => {
    // Registrar pagamento é restrito a admin (mesma regra da API do Financeiro).
    if (appt.status === "concluido" && appt.payment?.status === "pending" && isAdmin) {
      return { label: "Registrar pagamento", run: () => setPaying(appt) };
    }
    if (appt.status === "pendente") return { label: "Confirmar", run: () => confirmNow(appt) };
    if (appt.status === "confirmado") return { label: "Concluir", run: () => askConfirm(appt, "concluido") };
    return null;
  };

  const openMenu = (e, appt) => {
    e.stopPropagation();
    setMenu({ anchor: e.currentTarget, appt });
  };
  const closeMenu = () => setMenu({ anchor: null, appt: null });

  const summary = (
    <Typography variant="body2" className={classes.muted}>
      {todayAppts.length === 0
        ? "Nenhum agendamento hoje"
        : `Hoje: ${todayAppts.length} ${todayAppts.length === 1 ? "agendamento" : "agendamentos"}`}
      {todayPending > 0 && (
        <>
          {" · "}
          <span className={classes.warn}>{todayPending} a confirmar</span>
        </>
      )}
    </Typography>
  );

  // Arrastar para remarcar: o backend confere se o horário é livre e devolve erro se não for.
  const handleEventDrop = async ({ event, start, resourceId }) => {
    const appt = event.resource;
    if (resourceId && appt.user?.id && Number(resourceId) !== Number(appt.user.id)) {
      toast.info("Para trocar de profissional, crie um novo agendamento.");
      return;
    }
    if (moment(start).isSame(appt.startAt)) return;
    try {
      const { data } = await api.put(`/appointments/${appt.id}`, { startAt: moment(start).toISOString() });
      dispatch({ type: "UPDATE_APPOINTMENT", payload: data });
      setDetail((d) => (d && d.id === data.id ? data : d));
      toast.success("Agendamento remarcado.");
    } catch (err) {
      toastError(err);
    }
  };

  const teamResources = useMemo(
    () => (isAdmin ? providers.filter((p) => !providerFilter || String(p.id) === String(providerFilter)) : []),
    [isAdmin, providers, providerFilter]
  );

  const renderDetail = () => {
    if (!detail) return null;
    const terminal = TERMINAL_STATUSES.includes(detail.status);
    return (
      <>
        <div className={classes.drawerHead}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <StatusChip status={detail.status} />
            <Typography variant="caption" className={classes.muted}>
              #{detail.id}
            </Typography>
            <span style={{ flex: 1 }} />
            <IconButton size="small" aria-label="Fechar" onClick={() => setDetail(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <span className={`${classes.avatar} ${classes.avatarLg}`}>{initials(detail.contact?.name)}</span>
            <Box flex={1} minWidth={0}>
              <Typography component="h2" style={{ fontSize: 20, fontWeight: 700 }}>
                {detail.contact ? detail.contact.name : "—"}
              </Typography>
              {detail.contact?.number && (
                <Typography variant="body2" className={classes.muted}>
                  {formatPhone(detail.contact.number)}
                </Typography>
              )}
            </Box>
          </Box>
          {detail.contact?.id && (
            <Button
              variant="outlined"
              className={classes.footButton}
              style={{ flex: "none" }}
              onClick={() => history.push(`/contacts/${detail.contact.id}/profile`)}
            >
              Ver ficha do contato
            </Button>
          )}
        </div>

        <div className={classes.drawerBody}>
          <section>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <h3 className={classes.sectionTitle}>Agendamento</h3>
              {!terminal && (
                <Button
                  size="small"
                  color="primary"
                  startIcon={<EditOutlinedIcon style={{ fontSize: 16 }} />}
                  style={{ textTransform: "none", fontWeight: 600, marginBottom: 8 }}
                  onClick={() => setRescheduling(detail)}
                >
                  Remarcar
                </Button>
              )}
            </Box>
            <div className={classes.infoTable}>
              <div className={classes.infoRow}>
                <span className={classes.infoKey}>Quando</span>
                <span className={classes.infoVal}>
                  {`${WEEKDAYS[moment(detail.startAt).day()]}, ${moment(detail.startAt).format("DD/MM/YYYY")}`}
                </span>
                <span className={classes.infoSub}>
                  {moment(detail.startAt).format("HH:mm")}
                  {detail.endAt ? ` – ${moment(detail.endAt).format("HH:mm")}` : ""}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoKey}>Serviço</span>
                <span className={classes.infoVal}>{detail.service ? detail.service.name : "—"}</span>
                {detail.service?.durationMinutes && (
                  <span className={classes.infoSub}>{detail.service.durationMinutes} min</span>
                )}
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoKey}>Profissional</span>
                <span className={classes.infoVal}>{detail.user ? detail.user.name : "—"}</span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoKey}>Valor</span>
                <span className={classes.infoVal}>{formatMoney(priceOf(detail))}</span>
                {detail.status === "concluido" && detail.payment && (
                  <span
                    className={classes.infoSub}
                    style={{
                      fontWeight: 600,
                      color: detail.payment.status === "paid" ? (dark ? "#81C995" : "#1E6B33") : dark ? "#FFB74D" : "#9A4A0B",
                    }}
                  >
                    {detail.payment.status === "paid"
                      ? `Pago${detail.payment.paymentMethod ? ` · ${detail.payment.paymentMethod}` : ""}`
                      : "A receber"}
                  </span>
                )}
              </div>
            </div>
          </section>

          <section>
            <h3 className={classes.sectionTitle}>Observações</h3>
            <TextField
              multiline
              minRows={2}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Só a equipe vê"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              InputProps={{ style: { borderRadius: 10 } }}
            />
            {notes !== (detail.notes || "") && (
              <Box mt={1} display="flex" justifyContent="flex-end">
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disableElevation
                  disabled={savingNotes}
                  onClick={saveNotes}
                  style={{ textTransform: "none", fontWeight: 600, borderRadius: 8 }}
                >
                  {savingNotes ? <CircularProgress size={16} /> : "Salvar observações"}
                </Button>
              </Box>
            )}
          </section>

          {clientHistory && (
            <section>
              <h3 className={classes.sectionTitle}>Histórico do cliente</h3>
              <div className={classes.historyTiles}>
                <div className={classes.historyTile}>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{clientHistory.done}</div>
                  <div className={classes.subCell}>atendimentos</div>
                </div>
                <div className={classes.historyTile}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: clientHistory.missed ? (dark ? "#FF8A80" : "#B3261E") : undefined }}>
                    {clientHistory.missed}
                  </div>
                  <div className={classes.subCell}>{clientHistory.missed === 1 ? "falta" : "faltas"}</div>
                </div>
                <div className={classes.historyTile}>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>
                    {clientHistory.last ? moment(clientHistory.last).format("DD/MM") : "—"}
                  </div>
                  <div className={classes.subCell}>última visita</div>
                </div>
              </div>
            </section>
          )}

          {detail.status === "concluido" && detail.payment?.status === "pending" && isAdmin && (
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.primaryAction}
              onClick={() => setPaying(detail)}
            >
              Registrar pagamento
            </Button>
          )}

          {terminal && (
            <Typography variant="body2" className={classes.muted}>
              Este agendamento está <strong>{metaOf(detail.status).label.toLowerCase()}</strong> e não pode ser
              alterado.
            </Typography>
          )}
        </div>

        {!terminal && (
          <div className={classes.drawerFoot}>
            {detail.status === "pendente" && (
              <Button
                variant="contained"
                color="primary"
                disableElevation
                className={classes.primaryAction}
                startIcon={<CheckIcon />}
                onClick={() => confirmNow(detail)}
              >
                Confirmar presença
              </Button>
            )}
            <div className={classes.footRow}>
              <Button
                variant="outlined"
                className={classes.footButton}
                style={{ color: dark ? "#81C995" : "#1E6B33" }}
                onClick={() => askConfirm(detail, "concluido")}
              >
                Concluído
              </Button>
              <Button
                variant="outlined"
                className={classes.footButton}
                style={{ color: dark ? "#FF8A80" : "#B3261E" }}
                onClick={() => askConfirm(detail, "faltou")}
              >
                Faltou
              </Button>
              <Button
                className={classes.footButton}
                style={{ border: 0 }}
                onClick={() => askConfirm(detail, "cancelado")}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderRow = (appt) => {
    const action = rowAction(appt);
    const cancelled = appt.status === "cancelado";
    return (
      <div
        key={appt.id}
        className={classes.listRow}
        style={{ opacity: cancelled ? 0.6 : 1 }}
        onClick={() => setDetail(appt)}
      >
        <div style={{ gridArea: isMobile ? "time" : undefined }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{moment(appt.startAt).format("HH:mm")}</div>
          <div className={classes.subCell}>até {moment(appt.endAt).format("HH:mm")}</div>
        </div>
        <Box display="flex" alignItems="center" minWidth={0} style={{ gap: 10, gridArea: isMobile ? "who" : undefined }}>
          {!isMobile && <span className={classes.avatar} style={{ width: 32, height: 32, fontSize: 12 }}>{initials(appt.contact?.name)}</span>}
          <Box minWidth={0}>
            <div className={classes.nameCell} style={{ textDecoration: cancelled ? "line-through" : "none" }}>
              {appt.contact ? appt.contact.name : "—"}
            </div>
            {!isMobile && appt.contact?.number && (
              <div className={classes.subCell}>{formatPhone(appt.contact.number)}</div>
            )}
          </Box>
        </Box>
        <Box minWidth={0} style={{ gridArea: isMobile ? "svc" : undefined }}>
          <div style={{ fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {appt.service ? appt.service.name : "—"}
          </div>
          {!isMobile && appt.service?.durationMinutes && (
            <div className={classes.subCell}>{appt.service.durationMinutes} min</div>
          )}
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 8, fontSize: 13.5, gridArea: isMobile ? "pro" : undefined }}>
          {!isMobile && <span className={classes.avatar}>{providerInitials(appt)}</span>}
          <span className={classes.muted} style={isMobile ? { fontSize: 12 } : { color: "inherit" }}>
            {appt.user ? appt.user.name : "—"}
          </span>
        </Box>
        <div data-col="price" style={{ fontSize: 14, fontWeight: 600 }}>
          {formatMoney(priceOf(appt))}
          {appt.status === "concluido" && appt.payment && (
            <div
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color:
                  appt.payment.status === "paid" ? (dark ? "#81C995" : "#1E6B33") : dark ? "#FFB74D" : "#9A4A0B",
              }}
            >
              {appt.payment.status === "paid"
                ? `Pago${appt.payment.paymentMethod ? ` · ${appt.payment.paymentMethod}` : ""}`
                : "A receber"}
            </div>
          )}
        </div>
        <div style={{ gridArea: isMobile ? "status" : undefined }}>
          <StatusChip status={appt.status} />
        </div>
        <div className={classes.rowActions} style={{ gridArea: isMobile ? "actions" : undefined }}>
          {action && !isMobile && (
            <Button
              variant="outlined"
              size="small"
              className={classes.rowAction}
              onClick={(e) => {
                e.stopPropagation();
                action.run();
              }}
            >
              {action.label}
            </Button>
          )}
          {!TERMINAL_STATUSES.includes(appt.status) && (
            <IconButton size="small" className={classes.iconAction} aria-label="Mais ações" onClick={(e) => openMenu(e, appt)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      </div>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title="Atualizar agendamento"
        open={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, appt: null, status: null })}
        onConfirm={() => updateStatus(confirmModal.appt, confirmModal.status)}
      >
        {`Marcar este agendamento como "${confirmModal.status ? metaOf(confirmModal.status).label : ""}"?`}
      </ConfirmationModal>

      <Drawer
        anchor="right"
        open={!!detail}
        onClose={() => setDetail(null)}
        classes={{ paper: classes.drawerPaper }}
      >
        {renderDetail()}
      </Drawer>

      <NewAppointmentModal
        open={newModal.open}
        initialDate={newModal.date}
        defaultProviderId={!isAdmin ? user.id : providerFilter || undefined}
        onClose={() => setNewModal({ open: false, date: null })}
        onCreated={() => fetchRef.current && fetchRef.current()}
      />

      {paying && (
        <PaymentDialog
          appointment={paying}
          onClose={() => setPaying(null)}
          onDone={(payment) => {
            dispatch({ type: "UPDATE_APPOINTMENT", payload: { ...paying, payment } });
            setDetail((d) => (d && d.id === paying.id ? { ...d, payment } : d));
            setPaying(null);
          }}
        />
      )}

      {rescheduling && (
        <RescheduleDialog
          appointment={rescheduling}
          onClose={() => setRescheduling(null)}
          onDone={(updated) => {
            dispatch({ type: "UPDATE_APPOINTMENT", payload: updated });
            setDetail((d) => (d && d.id === updated.id ? updated : d));
            setRescheduling(null);
          }}
        />
      )}

      <Menu anchorEl={menu.anchor} open={!!menu.anchor} onClose={closeMenu}>
        {menu.appt && canTransitionTo(menu.appt.status, "concluido") && (
          <MenuItem onClick={() => { askConfirm(menu.appt, "concluido"); closeMenu(); }}>Marcar como concluído</MenuItem>
        )}
        {menu.appt && menu.appt.status === "pendente" && (
          <MenuItem onClick={() => { confirmNow(menu.appt); closeMenu(); }}>Confirmar</MenuItem>
        )}
        {menu.appt && canTransitionTo(menu.appt.status, "faltou") && (
          <MenuItem onClick={() => { askConfirm(menu.appt, "faltou"); closeMenu(); }}>Marcar como faltou</MenuItem>
        )}
        {menu.appt && canTransitionTo(menu.appt.status, "cancelado") && (
          <MenuItem onClick={() => { askConfirm(menu.appt, "cancelado"); closeMenu(); }}>Cancelar agendamento</MenuItem>
        )}
      </Menu>

      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <Title>Agenda</Title>
          {summary}
        </div>
        <div className={classes.headerButtons}>
          <div className={classes.viewToggle}>
            <button
              type="button"
              aria-pressed={viewMode === "calendar"}
              className={`${classes.viewButton} ${viewMode === "calendar" ? classes.viewButtonActive : ""}`}
              onClick={() => setViewMode("calendar")}
            >
              <CalendarTodayIcon fontSize="small" />
              Calendário
            </button>
            <button
              type="button"
              aria-pressed={viewMode === "list"}
              className={`${classes.viewButton} ${viewMode === "list" ? classes.viewButtonActive : ""}`}
              onClick={() => setViewMode("list")}
            >
              <ViewListIcon fontSize="small" />
              Lista
            </button>
          </div>
          <Button
            variant="outlined"
            className={`${classes.headerButton} ${classes.secondaryButton}`}
            startIcon={<BlockIcon />}
            onClick={() => history.push("/schedule-blocks")}
          >
            Bloquear horário
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.headerButton}
            startIcon={<AddIcon />}
            onClick={() => setNewModal({ open: true, date: null })}
          >
            Novo agendamento
          </Button>
        </div>
      </div>

      {viewMode === "calendar" && isMobile ? (
        <Paper className={classes.mainPaper} variant="outlined">
          <Box p={1.5} display="flex" flexDirection="column" style={{ gap: 12 }}>
            <Box display="flex" alignItems="center" style={{ gap: 4 }}>
              <IconButton size="small" aria-label="Semana anterior" onClick={() => setCalDate(moment(calDate).subtract(7, "days").toDate())}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography style={{ flex: 1, textAlign: "center", fontWeight: 700 }}>
                {calendarLabel("week", calDate)}
              </Typography>
              <IconButton size="small" aria-label="Próxima semana" onClick={() => setCalDate(moment(calDate).add(7, "days").toDate())}>
                <ChevronRightIcon />
              </IconButton>
            </Box>
            <div className={classes.weekStrip}>
              {Array.from({ length: 7 }, (_, i) => moment(calDate).startOf("week").add(i, "days")).map((d) => {
                const key = d.format("YYYY-MM-DD");
                const active = key === moment(calDate).format("YYYY-MM-DD");
                const has = calendarAppointments.some((a) => moment(a.startAt).format("YYYY-MM-DD") === key);
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={active}
                    className={`${classes.stripDay} ${active ? classes.stripDayActive : ""}`}
                    onClick={() => setCalDate(d.toDate())}
                  >
                    <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.85 }}>{WEEKDAYS[d.day()].toUpperCase()}</span>
                    <span style={{ fontSize: 17, fontWeight: 700 }}>{d.date()}</span>
                    <span
                      className={classes.dot}
                      style={{ width: 5, height: 5, backgroundColor: has ? (active ? "#fff" : theme.palette.primary.main) : "transparent" }}
                    />
                  </button>
                );
              })}
            </div>
            <div className={classes.filterLabel}>{dayLabel(moment(calDate).format("YYYY-MM-DD"))}</div>
            {calendarAppointments
              .filter((a) => moment(a.startAt).format("YYYY-MM-DD") === moment(calDate).format("YYYY-MM-DD"))
              .sort((x, y) => new Date(x.startAt) - new Date(y.startAt))
              .map((appt) => (
                <div key={appt.id} className={classes.mobileCard} onClick={() => setDetail(appt)}>
                  <Box width={48} flexShrink={0}>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{moment(appt.startAt).format("HH:mm")}</div>
                    <div className={classes.subCell}>{moment(appt.endAt).format("HH:mm")}</div>
                  </Box>
                  <span style={{ width: 3, borderRadius: 2, backgroundColor: metaOf(appt.status).color, flexShrink: 0 }} />
                  <Box minWidth={0} flex={1} display="flex" flexDirection="column" style={{ gap: 3 }}>
                    <div className={classes.nameCell} style={{ fontSize: 15 }}>{appt.contact ? appt.contact.name : "—"}</div>
                    <div className={classes.subCell} style={{ fontSize: 13 }}>
                      {appt.service ? appt.service.name : "—"} · {appt.user ? appt.user.name : "—"}
                    </div>
                    <span style={{ alignSelf: "flex-start" }}><StatusChip status={appt.status} /></span>
                  </Box>
                </div>
              ))}
            {!loading &&
              !calendarAppointments.some((a) => moment(a.startAt).format("YYYY-MM-DD") === moment(calDate).format("YYYY-MM-DD")) && (
                <Box className={classes.emptyState}>
                  <EventBusyIcon className={classes.emptyIcon} />
                  <Typography variant="body2">Nenhum agendamento neste dia.</Typography>
                </Box>
              )}
          </Box>
        </Paper>
      ) : viewMode === "calendar" ? (
        <div className={classes.body}>
          <Paper className={classes.mainPaper} variant="outlined">
            {(isAdmin && providers.length > 0) && (
              <div className={classes.filterBar}>
                <ProviderPills />
                <span style={{ flex: 1 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={showCancelled}
                      onChange={(e) => setShowCancelled(e.target.checked)}
                    />
                  }
                  label={<span style={{ fontSize: 13 }}>Mostrar cancelados</span>}
                />
              </div>
            )}
            <div className={classes.calendarWrapper}>
              <DnDCalendar
                localizer={localizer}
                culture="pt-BR"
                messages={calendarMessages}
                events={calendarEvents}
                backgroundEvents={backgroundEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: "100%" }}
                view={calView}
                date={calDate}
                onView={setCalView}
                onNavigate={setCalDate}
                onSelectEvent={(event) => setDetail(event.resource)}
                selectable
                resizable={false}
                draggableAccessor={(e) => !TERMINAL_STATUSES.includes(e.resource?.status)}
                onEventDrop={handleEventDrop}
                resources={teamMode && calView === "day" && teamResources.length > 0 ? teamResources : undefined}
                resourceIdAccessor="id"
                resourceTitleAccessor="name"
                onSelectSlot={({ start }) => {
                  if (moment(start).isBefore(moment().startOf("day"))) return;
                  setNewModal({ open: true, date: moment(start).format("YYYY-MM-DD") });
                }}
                eventPropGetter={eventStyleGetter}
                components={{ event: AppointmentEvent, toolbar: CalendarToolbar }}
                views={["month", "week", "day", "agenda"]}
                min={new Date(0, 0, 0, 7, 0, 0)}
                max={new Date(0, 0, 0, 22, 0, 0)}
                scrollToTime={new Date(0, 0, 0, 8, 0, 0)}
                popup
              />
            </div>
            <div className={classes.legend}>
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <span key={key} className={classes.legendItem}>
                  <span className={classes.dot} style={{ backgroundColor: meta.color, width: 8, height: 8 }} />
                  {meta.label}
                </span>
              ))}
            </div>
          </Paper>

          <aside className={classes.side}>
            <Paper className={`${classes.sideCard} ${classes.stats}`} variant="outlined">
              <div className={classes.statsTitle}>Hoje · {WEEKDAYS[moment().day()]}, {moment().date()} {MONTHS[moment().month()]}</div>
              <div>
                <div className={classes.statValue}>{todayAppts.length}</div>
                <div className={classes.statLabel}>agendados</div>
              </div>
              <div>
                <div className={classes.statValue} style={{ color: dark ? "#81C995" : "#1E6B33" }}>{todayDone}</div>
                <div className={classes.statLabel}>concluídos</div>
              </div>
              <div>
                <div className={classes.statValue}>{formatMoney(todayValue)}</div>
                <div className={classes.statLabel}>previsto</div>
              </div>
            </Paper>

            <Paper className={`${classes.sideCard} ${classes.pendingCard}`} variant="outlined">
              <div className={classes.pendingHead}>
                <span className={classes.dot} style={{ backgroundColor: STATUS_META.pendente.color, width: 8, height: 8 }} />
                <h2>A confirmar</h2>
                <span className={classes.countBadge}>{pendingList.length}</span>
              </div>
              <div className={classes.pendingList}>
                {pendingList.length === 0 && !loading && (
                  <Typography variant="body2" className={classes.muted} style={{ padding: 16, textAlign: "center" }}>
                    Nada pendente neste período.
                  </Typography>
                )}
                {pendingList.map((appt) => (
                  <div key={appt.id} className={classes.pendingItem} onClick={() => setDetail(appt)}>
                    <Box display="flex" style={{ gap: 10 }}>
                      <Box width={56} flexShrink={0}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{moment(appt.startAt).format("HH:mm")}</div>
                        <div className={classes.subCell}>
                          {moment(appt.startAt).format("YYYY-MM-DD") === todayKey
                            ? "hoje"
                            : `${WEEKDAYS[moment(appt.startAt).day()]}, ${moment(appt.startAt).date()}`}
                        </div>
                      </Box>
                      <Box minWidth={0} flex={1}>
                        <div className={classes.nameCell} style={{ fontSize: 13.5 }}>{appt.contact ? appt.contact.name : "—"}</div>
                        <div className={classes.subCell} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {appt.service ? appt.service.name : "—"} · {appt.user ? appt.user.name : "—"}
                        </div>
                      </Box>
                    </Box>
                    <Button
                      fullWidth
                      className={classes.confirmButton}
                      startIcon={<CheckIcon style={{ fontSize: 16 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmNow(appt);
                      }}
                    >
                      Confirmar
                    </Button>
                  </div>
                ))}
              </div>
            </Paper>
          </aside>
        </div>
      ) : (
        <Paper className={classes.mainPaper} variant="outlined">
          <div className={classes.toolbar}>
            <TextField
              className={classes.searchField}
              placeholder="Buscar por cliente, telefone ou serviço"
              type="search"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ "aria-label": "Buscar agendamentos" }}
            />
            <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 8 }}>
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  aria-pressed={period === p.key}
                  className={`${classes.pill} ${period === p.key ? classes.pillActive : ""}`}
                  onClick={() => setPeriod(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </Box>
          </div>

          <div className={classes.filterBar}>
            {STATUS_TABS.map((t) => (
              <button
                key={t.key || "all"}
                type="button"
                aria-pressed={statusFilter === t.key}
                className={`${classes.pill} ${statusFilter === t.key ? classes.pillActive : ""}`}
                onClick={() => setStatusFilter(t.key)}
              >
                {t.key && (
                  <span className={classes.dot} style={{ backgroundColor: STATUS_META[t.key].color }} />
                )}
                {t.label} <span className={classes.pillCount}>{counts[t.key] || 0}</span>
              </button>
            ))}
            {isAdmin && providers.length > 0 && (
              <>
                <span style={{ flex: 1 }} />
                <ProviderPills />
              </>
            )}
          </div>

          <div className={classes.listHeaderRow}>
            <span>Horário</span>
            <span>Cliente</span>
            <span>Serviço</span>
            <span>Profissional</span>
            <span>Valor</span>
            <span>Status</span>
            <span style={{ textAlign: "right" }}>Ações</span>
          </div>

          <div className={classes.tableWrapper}>
            {loading && appointments.length === 0 && (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!loading && groups.length === 0 && (
              <Box className={classes.emptyState}>
                <EventBusyIcon className={classes.emptyIcon} />
                <Typography variant="body2">Nenhum agendamento no período selecionado.</Typography>
              </Box>
            )}
            {groups.map((g) => {
              const isCollapsed = !!collapsed[g.key];
              return (
                <div key={g.key}>
                  <button
                    type="button"
                    aria-expanded={!isCollapsed}
                    className={classes.groupHeader}
                    onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
                  >
                    {isCollapsed ? <ChevronRightSmallIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    {g.label}
                    <span className={classes.groupBadge}>{g.rows.length}</span>
                  </button>
                  {!isCollapsed && g.rows.map(renderRow)}
                </div>
              );
            })}
          </div>

          <div className={classes.footer}>
            <Box display="flex" flexWrap="wrap" style={{ gap: 16 }}>
              <span>
                {listAppointments.length} {listAppointments.length === 1 ? "agendamento" : "agendamentos"}
              </span>
              <span style={{ flex: 1 }} />
              <span>
                Concluídos: <strong style={{ color: theme.palette.text.primary }}>{formatMoney(listTotals.done)}</strong>
              </span>
            </Box>
          </div>
        </Paper>
      )}
    </MainContainer>
  );
};

export default AppointmentsPage;
